<template>
    <div class="navigation-section" ref="navWrapper">
        <nav>
            <ul>
                <li v-for="(section, index) in sections" :key="index" class="nav-item">
                    <a
                        :href="`#${section.id}`"
                        :class="{ [CLASS_NAME_ACTIVE]: section.id === activeSection }"
                        @click.prevent="scrollToSection(section.id)"
                        :aria-current="section.id === activeSection ? 'true' : null"
                    >
                        {{ section.text }}
                    </a>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, nextTick } from 'vue';

// Constants
const SELECTOR_LINKS = '[href]';
const CLASS_NAME_ACTIVE = 'active';

// Refs
const navWrapper = ref(null);
const activeSection = ref(null);
const sections = ref([]);

// Private state
const sectionsMap = reactive(new Map());
const previousScrollData = reactive({
    visibleEntryTop: 0,
    parentScrollTop: 0,
});

// Find all elements with data-section-navigation attribute
const findSections = () => {
    const elements = document.querySelectorAll('[data-section-navigation]');

    sections.value = Array.from(elements).map(el => {
        // Generate ID if none exists
        if (!el.id) {
            el.id = `section-${Math.random().toString(36).substring(2, 9)}`;
        }

        return {
            id: el.id,
            text: el.getAttribute('data-section-navigation'),
            element: el,
        };
    });

    // Populate maps for quick lookups
    sections.value.forEach(section => {
        sectionsMap.set(section.id, section.element);
    });
};

// Set up intersection observer
const initializeObserver = () => {
    const observer = new IntersectionObserver(observerCallback, {
        rootMargin: '0px 0px -25%',
        threshold: [0.1, 0.5, 1],
    });

    sections.value.forEach(section => {
        observer.observe(section.element);
    });

    return observer;
};

// Observer callback
const observerCallback = (entries) => {
    const parentScrollTop = document.documentElement.scrollTop;
    const userScrollsDown = parentScrollTop >= previousScrollData.parentScrollTop;
    previousScrollData.parentScrollTop = parentScrollTop;

    for (const entry of entries) {
        // Skip non-intersecting entries
        if (!entry.isIntersecting) {
            continue;
        }

        const entryId = entry.target.id;
        const entryIsLowerThanPrevious = entry.target.offsetTop >= previousScrollData.visibleEntryTop;

        // If scrolling down, pick the biggest offsetTop
        if (userScrollsDown && entryIsLowerThanPrevious) {
            previousScrollData.visibleEntryTop = entry.target.offsetTop;
            activeSection.value = entryId;

            // If parent isn't scrolled, keep the first visible item
            if (!parentScrollTop) {
                return;
            }
            continue;
        }

        // If scrolling up, pick the smallest offsetTop
        if (!userScrollsDown && !entryIsLowerThanPrevious) {
            previousScrollData.visibleEntryTop = entry.target.offsetTop;
            activeSection.value = entryId;
        }
    }
};

// Scroll to section when its navigation link is clicked
const scrollToSection = (sectionId) => {
    if (!sectionsMap.has(sectionId)) {
        console.error(`Section with id "${sectionId}" does not exist`);
        return;
    }

    sectionsMap.get(sectionId).scrollIntoView({ behavior: 'smooth' });
    activeSection.value = sectionId;
};

// Scroll navigation to make active item visible
const scrollActiveLinkIntoView = () => {
    if (!activeSection.value || !navWrapper.value) {
        return;
    }

    // Find the active link element
    const activeLink = navWrapper.value.querySelector(`${SELECTOR_LINKS}.${CLASS_NAME_ACTIVE}`);
    if (!activeLink) {
        return;
    }

    const navWrapperRect = navWrapper.value.getBoundingClientRect();
    const activeLinkRect = activeLink.getBoundingClientRect();

    // Do not scroll if there is no overflow
    if (navWrapper.value.scrollWidth <= navWrapper.value.clientWidth) {
        return;
    }

    // Check if active link is not fully visible
    if (activeLinkRect.left < navWrapperRect.left || activeLinkRect.right > navWrapperRect.right) {
        let offset = 0;

        // If the active link width is larger than the wrapper width, scroll to the left edge
        if (activeLink.offsetWidth > navWrapper.value.clientWidth) {
            offset = activeLink.offsetLeft;
        } else {
            // Otherwise, scroll to center active link
            offset = activeLink.offsetLeft - (navWrapper.value.clientWidth / 2) + (activeLink.offsetWidth / 2);
        }

        // Ensure we don't scroll past the ends
        offset = Math.max(0, Math.min(offset, navWrapper.value.scrollWidth - navWrapper.value.clientWidth));

        navWrapper.value.scrollTo({ left: offset, behavior: 'smooth' });
    }
};

// Watch for changes in active section
watch(activeSection, () => {
    nextTick(() => {
        scrollActiveLinkIntoView();
    });
});

// Set up all listeners and observers
let intersectionObserver;

onMounted(() => {
    findSections();
    intersectionObserver = initializeObserver();
});

onUnmounted(() => intersectionObserver?.disconnect());
</script>
